import { IconButton, Spacer, Split, SplitSection, SvgMenu } from '@loomhq/lens';
import cn from 'classnames';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

// Remove when notifications moves to sidebar
import { selectInTrimMode } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import useDowntimeBanner from 'hooks/useDowntimeBanner';
import useTheme from 'hooks/useTheme';

import { AnonymousHeader } from './anonymous-header';

import SearchBar from 'components/destination-search/search-bar';
import DownloadProgress from 'components/download-progress';
import HelpBubble from 'components/help-bubble';
import HideDuringDowntime from 'components/hide-during-downtime';
import HeaderPortalDestination from 'components/layout/header/portal-destination';
import { BubbleMenu, ProfileBubb } from 'components/profile-bubble';
import { useShouldShowWaveform } from 'components/share-video/edit-tools-old/hooks';

import styles from './styles.module.less';

type HeaderV2Props = {
  setNavBarIsOpenMobile: () => void;
};

export const HeaderV2 = ({
  setNavBarIsOpenMobile,
}: HeaderV2Props): JSX.Element => {
  const [menu, setMenu] = useState<string | null>(null);
  const isUserLoggedIn = useSelector(selectUserIsLogged);
  const isInTrimMode = useSelector(selectInTrimMode);

  useDowntimeBanner();
  useTheme();

  const shouldShowWaveform = useShouldShowWaveform();

  const onBubbleMenuExit = useCallback(() => {
    setTimeout(() => {
      document.body.classList.remove('noscroll');
      setMenu(null);
    }, 150);
  }, [setMenu]);

  const onBubbleMenuOpen = () => setMenu('bubb');

  // Header for non-logged in users
  if (!isUserLoggedIn) {
    return <AnonymousHeader setNavBarIsOpenMobile={setNavBarIsOpenMobile} />;
  }

  const isBubb = menu === 'bubb';

  return (
    <>
      <header className={cn(styles.header, 'sticky top:0 bgc:background')}>
        {isInTrimMode && shouldShowWaveform && (
          <div
            className={cn(
              styles.headerOverlay,
              'absolute top:0 left:0 width:full height:full bgc:overlay'
            )}
          ></div>
        )}

        <DownloadProgress />

        <ul className={cn(styles.headerBar, 'items:center')}>
          <li className={cn(styles.headerBarItem)}>
            <div className={cn(styles.mobileMenu, 'mr:auto')}>
              <Spacer right="small">
                <IconButton
                  altText="Toggle library navigation"
                  icon={<SvgMenu />}
                  onClick={() => setNavBarIsOpenMobile()}
                />
              </Spacer>
            </div>
          </li>
          <li
            className={cn(styles.searchBar, styles.headerBarItem, 'py:medium')}
          >
            <SearchBar />
          </li>
          <li
            className={cn(styles.headerBarItem, 'py:medium items:center flex')}
          >
            <HeaderPortalDestination />
          </li>
          <li className={cn(styles.headerBarItem)}>
            <HideDuringDowntime>
              <div className="p:medium">
                <Split gap="medium" wrap="nowrap">
                  <SplitSection>
                    <ul className={cn(styles.actions, 'flex items:center')}>
                      <li>
                        <ProfileBubb onOpen={onBubbleMenuOpen} />
                      </li>
                    </ul>
                  </SplitSection>
                </Split>
                {isBubb && <BubbleMenu onExit={onBubbleMenuExit} />}
                {isUserLoggedIn && <HelpBubble />}
              </div>
            </HideDuringDowntime>
          </li>
        </ul>
      </header>
    </>
  );
};
